import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Lock, Mail, User } from 'lucide-react';
import { signUp } from '../lib/auth';
import { subscriptionService } from '../lib/subscriptionService';
import toast from 'react-hot-toast';

function Register() {
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    email: '',
    password: '',
    confirmPassword: '',
    acceptTerms: false
  });

  const [errors, setErrors] = useState<Record<string, string>>({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [paywallEnabled, setPaywallEnabled] = useState(false);
  
  // Check paywall settings when component mounts
  useEffect(() => {
    const checkPaywallSettings = async () => {
      try {
        const settings = await subscriptionService.getPaywallSettings();
        setPaywallEnabled(settings.paywall_enabled);
      } catch (error) {
        console.error('Error checking paywall settings:', error);
      }
    };
    
    checkPaywallSettings();
  }, []);

  const validateForm = () => {
    const newErrors: Record<string, string> = {};
    
    if (!formData.email) {
      newErrors.email = 'Email is required';
    } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
      newErrors.email = 'Please enter a valid email address';
    }
    
    if (!formData.password) {
      newErrors.password = 'Password is required';
    } else if (formData.password.length < 8) {
      newErrors.password = 'Password must be at least 8 characters long';
    }
    
    if (formData.password !== formData.confirmPassword) {
      newErrors.confirmPassword = 'Passwords do not match';
    }
    
    if (!formData.acceptTerms) {
      newErrors.acceptTerms = 'You must accept the terms and conditions';
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!validateForm() || isSubmitting) return;

    setIsSubmitting(true);
    // Show a loading toast that will be updated based on the result
    const loadingToastId = toast.loading('Creating your account...');
    
    try {
      // Log registration attempt
      console.log('Starting registration process for email:', formData.email);
      
      await signUp({
        email: formData.email,
        password: formData.password,
      });
      
      // Determine registration message based on paywall settings
      const successMessage = paywallEnabled
        ? 'Registration initiated! Please check your email to verify your account. After verification, you will need to select a subscription plan.'
        : 'Registration successful! Please check your email for a confirmation link.';
      
      // Update the loading toast with success message
      toast.success(successMessage, { id: loadingToastId, duration: 8000 });
      
      // Show additional instructions if paywall is enabled
      if (paywallEnabled) {
        console.log('Registration completed with paywall enabled - user will be directed to subscription after email verification');
        // Don't navigate anywhere yet - wait for email verification first
      } else {
        // Paywall not enabled - can go directly to account after verification
        console.log('Registration completed without paywall - user will go to account after email verification');
      }
      
      // Redirect to a confirmation page with clear instructions
      navigate('/auth/confirmation', { 
        state: { 
          email: formData.email, 
          paywallEnabled: paywallEnabled 
        } 
      });
    } catch (error: any) {
      console.error('Registration error:', error);
      
      // Add detailed console logging
      console.error('Registration error details:', {
        message: error.message,
        code: error.code,
        name: error.name,
        status: error.status
      });
      
      // Log email service errors for monitoring
      if (error.message && (
          error.message.includes('Too many concurrent SMTP connections') ||
          error.message.includes('Error sending confirmation email') ||
          error.message.includes('rate limit') ||
          error.message.toLowerCase().includes('smtp') ||
          error.message.includes('email') ||
          error.message.includes('timeout') ||
          error.message.includes('deadline exceeded')
      )) {
        console.error('Email service error detected during registration:', error.message);
      }
      
      // Check for specific email service errors
      if (error.message && (
          error.message.includes('Too many concurrent SMTP connections') ||
          error.message.includes('Error sending confirmation email') ||
          error.message.includes('rate limit') ||
          error.message.toLowerCase().includes('smtp') ||
          error.message.includes('email') ||
          error.message.includes('timeout') ||
          error.message.includes('deadline exceeded')
      )) {
        // For timeout errors, show a more specific message
        if (error.message.includes('timeout') || error.message.includes('deadline exceeded')) {
          setErrors(prev => ({
            ...prev,
            submit: 'Our verification service is experiencing delays. Please try again in a few minutes.'
          }));
          toast.error(
            'Our verification service is experiencing temporary delays. Please try again in a few minutes, ' +
            'or contact support if you do not receive a verification email within 24 hours.', 
            { duration: 8000 }
          );
        } else {
          // For other email-related errors
          setErrors(prev => ({
            ...prev,
            submit: 'Registration email service is currently busy. Please try again in a few minutes.'
          }));
          toast.error('Email service is busy, please try again in a few minutes.');
        }
        
        // Update the loading toast to error state
        toast.dismiss(loadingToastId);
      } else {
        // Generic error handling
        setErrors(prev => ({
          ...prev,
          submit: 'Registration failed. Please try again.'
        }));
        toast.error('Registration failed: ' + (error.message || 'Unknown error'));
        toast.dismiss(loadingToastId);
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  return (
    <div className="min-h-screen bg-gray-50 flex flex-col justify-center py-12 px-4 sm:px-6 lg:px-8">
      <div className="sm:mx-auto sm:w-full sm:max-w-md">
        <div className="text-center">
          <User className="mx-auto h-12 w-12 text-rose-600" />
          <h2 className="mt-6 text-center text-3xl font-bold tracking-tight text-gray-900">
            Create your account
          </h2>
          <p className="mt-2 text-center text-sm text-gray-600">
            Join WigConnection's trusted marketplace for premium wigs
          </p>
          <p className="mt-2 text-center text-sm text-gray-600">
            Already have an account?{' '}
            <Link to="/login" className="font-medium text-rose-600 hover:text-rose-500">
              Sign in
            </Link>
          </p>
        </div>

        <div className="mt-8 bg-white py-8 px-4 shadow-md sm:rounded-lg sm:px-10">
          {errors.submit && (
            <div className="mb-4 p-4 bg-red-50 border border-red-200 rounded-md">
              <p className="text-sm text-red-600">{errors.submit}</p>
            </div>
          )}

          <form className="space-y-6" onSubmit={handleSubmit}>
            <div>
              <label htmlFor="email" className="block text-sm font-medium text-gray-700">
                Email address
              </label>
              <div className="mt-1 relative">
                <input
                  id="email"
                  name="email"
                  type="email"
                  autoComplete="email"
                  required
                  className="appearance-none block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm placeholder-gray-400 focus:outline-none focus:ring-rose-500 focus:border-rose-500"
                  value={formData.email}
                  onChange={handleChange}
                />
                <Mail className="absolute right-3 top-2.5 h-5 w-5 text-gray-400" />
              </div>
              {errors.email && (
                <p className="mt-2 text-sm text-red-600">{errors.email}</p>
              )}
            </div>

            <div>
              <label htmlFor="password" className="block text-sm font-medium text-gray-700">
                Password
              </label>
              <div className="mt-1 relative">
                <input
                  id="password"
                  name="password"
                  type="password"
                  autoComplete="new-password"
                  required
                  className="appearance-none block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm placeholder-gray-400 focus:outline-none focus:ring-rose-500 focus:border-rose-500"
                  value={formData.password}
                  onChange={handleChange}
                />
                <Lock className="absolute right-3 top-2.5 h-5 w-5 text-gray-400" />
              </div>
              {errors.password && (
                <p className="mt-2 text-sm text-red-600">{errors.password}</p>
              )}
            </div>

            <div>
              <label htmlFor="confirmPassword" className="block text-sm font-medium text-gray-700">
                Confirm Password
              </label>
              <div className="mt-1 relative">
                <input
                  id="confirmPassword"
                  name="confirmPassword"
                  type="password"
                  autoComplete="new-password"
                  required
                  className="appearance-none block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm placeholder-gray-400 focus:outline-none focus:ring-rose-500 focus:border-rose-500"
                  value={formData.confirmPassword}
                  onChange={handleChange}
                />
                <Lock className="absolute right-3 top-2.5 h-5 w-5 text-gray-400" />
              </div>
              {errors.confirmPassword && (
                <p className="mt-2 text-sm text-red-600">{errors.confirmPassword}</p>
              )}
            </div>

            <div className="flex items-start">
              <div className="flex items-center h-5">
                <input
                  id="acceptTerms"
                  name="acceptTerms"
                  type="checkbox"
                  className="h-4 w-4 text-rose-600 focus:ring-rose-500 border-gray-300 rounded"
                  checked={formData.acceptTerms}
                  onChange={handleChange}
                />
              </div>
              <div className="ml-3">
                <label htmlFor="acceptTerms" className="text-sm text-gray-700">
                  I agree to the{' '}
                  <Link to="/legal/terms" className="text-rose-600 hover:text-rose-500">
                    Terms and Conditions
                  </Link>
                </label>
                {errors.acceptTerms && (
                  <p className="mt-1 text-sm text-red-600">{errors.acceptTerms}</p>
                )}
              </div>
            </div>

            <div className="text-sm text-gray-500 mt-4">
              <p>After registration, you'll receive an email to confirm your account.</p>
              <p className="mt-1">Please check your inbox and click the confirmation link to activate your account.</p>
            </div>

            <div>
              <button
                type="submit"
                disabled={isSubmitting}
                className="w-full flex justify-center py-2 px-4 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-rose-600 hover:bg-rose-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-rose-500 disabled:opacity-50"
              >
                {isSubmitting ? 'Creating Account...' : 'Create Account'}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
}

export default Register;